@if (activeQuestion()) {
  <form [formGroup]="activeQuestion()" class="question-form__container">
    <mat-card class="question-form__card">
      <mat-card-title>
        <div class="title">
          <div
            class="eiq-id-badge"
            #tooltip="matTooltip"
            matTooltip="This number has already been used. Try another."
            matTooltipPosition="below"
            [matTooltipDisabled]="!activeQuestion().get('code').hasError('duplicated')"
          >
            <fa-icon [icon]="questionTypeConfig[activeQuestion().value.type].icon"></fa-icon>
            <input
              #codeInput
              textTrim
              name="question-form__code-input"
              class="question-form__code-input"
              formControlName="code"
              maxlength="40"
              [class.invalid]="activeQuestion().get('code').invalid"
              [style.width]="activeQuestion().get('code').value.length + 1 + 'ch'"
              (keydown.enter)="codeInput.blur()"
              (blur)="onCheckDuplicate()"
            />
          </div>
          @if (typeChangeOptions$().length > 0) {
            <eiq-button
              [text]="questionTypeConfig[activeQuestion().value.type].text"
              [icon]="typeChangeTrigger.menuOpen ? faChevronUp : faChevronDown"
              [iconPosition]="'right'"
              [color]="'light'"
              [style]="'basic'"
              [size]="'large'"
              [title]="'Change question type'"
              [testId]="'question-form-change-type'"
              #typeChangeTrigger="matMenuTrigger"
              [matMenuTriggerFor]="typeChangeMenu"
            ></eiq-button>

            <mat-menu #typeChangeMenu="matMenu">
              <span class="question-form__type-change-header text__xs fw__500 label">CHANGE QUESTION TYPE</span>
              @for (targetType of typeChangeOptions$(); track targetType) {
                <button mat-menu-item [attr.data-testid]="'question-form-change-type-' + targetType" (click)="onChangeQuestionType(targetType)">
                  <fa-icon [icon]="questionTypeConfig[targetType].icon"></fa-icon>
                  <span class="fs__sm fw__500">{{ questionTypeConfig[targetType].text }}</span>
                </button>
              }
            </mat-menu>
          } @else {
            <span class="question-form__type-change-label fs__md fw__500">{{ questionTypeConfig[activeQuestion().value.type].text }}</span>
          }
        </div>

        @if (activeQuestion().value.programmerNotes) {
          <empathy-programmer-notes [programmerNotes]="activeQuestion().value.programmerNotes"></empathy-programmer-notes>
        }
        <div class="actions">
          <eiq-button [matMenuTriggerFor]="menu" [title]="'Open commands menu'" [icon]="faEllipsisVertical" [color]="'light'" [style]="'fab'"></eiq-button>

          <mat-menu #menu="matMenu">
            @if (isEndQuestionType$()) {
              <ng-container *ngTemplateOutlet="saveContainer"></ng-container>
              <ng-container *ngTemplateOutlet="deleteContainer"></ng-container>
            } @else if (isHiddenQuestionType$()) {
              <ng-container *ngTemplateOutlet="deleteContainer"></ng-container>
            } @else {
              <ng-container *ngTemplateOutlet="saveContainer"></ng-container>

              <button mat-menu-item (click)="duplicateQuestion.emit()">
                <fa-icon [icon]="faClone"></fa-icon>
                <span class="fs__sm fw__500">Duplicate</span>
              </button>

              <ng-container *ngTemplateOutlet="deleteContainer"></ng-container>
            }

            <ng-template #saveContainer>
              <button mat-menu-item color="light" [matMenuTriggerFor]="menu">
                <fa-icon [icon]="faBookmark"></fa-icon>
                <span class="fs__sm fw__500">Save to Library</span>
              </button>

              <mat-menu #menu="matMenu">
                <button mat-menu-item (click)="saveQuestionOnLibrary.emit()">
                  <fa-icon [icon]="faBookmark"></fa-icon>
                  <span>Save question</span>
                </button>
                <button mat-menu-item (click)="saveOptionsOnLibrary.emit()">
                  <fa-icon [icon]="faBookmark"></fa-icon>
                  <span>Save options</span>
                </button>
              </mat-menu>
            </ng-template>

            <ng-template #deleteContainer>
              <button mat-menu-item color="warn" (click)="remove.emit()">
                <fa-icon [icon]="faTrashCan"></fa-icon>
                <span class="fs__sm fw__500">Delete</span>
              </button>
            </ng-template>
          </mat-menu>
        </div>
      </mat-card-title>
      <mat-card-content>
        <div class="question-form__actions">
          <div class="question-form__actions__buttons">
            <span
              matTooltip="Only 1 stimulus is allowed"
              matTooltipPosition="below"
              [matTooltipDisabled]="allowMultipleStimulus$() || (activeQuestion().get('stimulus') | toFormArray).controls.length === 0"
            >
              <button
                mat-icon-button
                title="Add stimulus"
                [color]="'light'"
                [disabled]="!allowMultipleStimulus$() && (activeQuestion().get('stimulus') | toFormArray).controls.length > 0"
                data-testid="question-form-add-stimulus"
                (click)="onAddStimulus()"
              >
                <fa-icon [icon]="faPhotoFilm" matTooltip="Add stimulus" matTooltipPosition="below"></fa-icon>
              </button>
            </span>

            <ng-content select="[slot=tabs]"></ng-content>
          </div>

          @if (!isEndQuestionType$() && !isInfoQuestionType$() && !isHiddenQuestionType$()) {
            <mat-checkbox color="primary" formControlName="requiredInd" [checked]="activeQuestion().value.requiredInd" [labelPosition]="'before'">
              <span class="fs__sm fw__500">Required</span>
            </mat-checkbox>
          }
        </div>

        <div class="question-form__content">
          @if (isEndQuestionType$()) {
            <div class="question-form__content__common">
              <div class="editor-question-form__input">
                <span class="label fs__md">Title</span>
                <empathy-text-editor [id]="activeQuestion().value.id + '_title'" [control]="activeQuestion().get('title')"></empathy-text-editor>
              </div>

              <div class="editor-question-form__input">
                <span class="label fs__md">
                  Description
                  <span class="fs__xs caption">(Optional)</span>
                </span>

                <empathy-text-editor id="description" [control]="activeQuestion().get('description')"></empathy-text-editor>
              </div>

              @if (activeQuestion().value.visibleWhen.length === 0) {
                <div class="question-form__content__warning">
                  <span class="fs__md">
                    This type of question must have at least one condition configured. Click
                    <button mat-icon-button title="Open Conditions" color="primary" (click)="onOpenSideTab(sideTabs.Conditions)">
                      <fa-icon [icon]="faCodeBranch"></fa-icon>
                    </button>
                    to open the conditional builder
                  </span>
                </div>
              }
            </div>
          } @else {
            <div class="question-form__content__common">
              @if ((activeQuestion().get('stimulus') | toFormArray).controls.length > 0) {
                <empathy-shared-stimulus
                  [stimulus]="activeQuestion().get('stimulus').value"
                  [height]="130"
                  [width]="130"
                  (delete)="onDeleteMedia($event)"
                ></empathy-shared-stimulus>
              }

              @if (activeQuestion().get('type').value === questionType.AIChat) {
                <div class="editor-question-form__input">
                  <span class="label fs__md">Starting Question</span>
                  <empathy-text-editor
                    [id]="activeQuestion().value.id + '_starting-question'"
                    [control]="activeQuestion().get('startingQuestion')"
                  ></empathy-text-editor>
                </div>
              } @else {
                <div class="editor-question-form__input">
                  <span class="label fs__md">Question</span>
                  <empathy-text-editor [id]="activeQuestion().value.id + '_title'" [control]="activeQuestion().get('title')"></empathy-text-editor>
                </div>
              }

              <div class="editor-question-form__input">
                <span class="label fs__md">Add Notes: <span class="fs__xs caption">(Optional)</span></span>
                <empathy-text-editor [id]="activeQuestion().value.id + '_description'" [control]="activeQuestion().get('description')"></empathy-text-editor>
              </div>
            </div>

            @if (questionTypeFormComponent[activeQuestion().value.type]) {
              <mat-divider class="question-form__divider"></mat-divider>
              <ng-container *ngComponentOutlet="questionTypeFormComponent[activeQuestion().value.type]"></ng-container>
            }
          }
        </div>
      </mat-card-content>
    </mat-card>
  </form>
}
